import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, ErrorAlert, Spinner } from '../components/ui';
import { usePagination } from '../lib/usePagination';

type Any = Record<string, any>;

const TYPE_LABELS: Record<string, string> = {
  production: 'Produkcja',
  visual: 'Grafika / social',
  'package-item': 'Element pakietu',
  article: 'Artykuł',
};

const KIND_LABELS: Record<string, string> = {
  EDU_SHORT: 'EDU Short',
  RADAR_SHORT: 'Radar Short',
  LONG_VIDEO: 'Long',
  DAILY_BRIEFING: 'Daily Briefing',
  WEEKLY_RECAP: 'Weekly Recap',
  SOCIAL_IMAGE: 'Grafika social',
  ARTICLE: 'Artykuł',
  SHORT: 'Short',
  LONG: 'Long',
};

function statusTone(status: string): 'neutral' | 'info' | 'warning' | 'success' | 'danger' {
  if (['APPROVED', 'READY', 'PUBLISHED', 'SCHEDULED', 'DONE'].includes(status)) return 'success';
  if (['READY_FOR_REVIEW', 'NEEDS_REVIEW', 'SCRIPT_READY', 'QUEUED', 'RENDERING', 'CAPTURING'].includes(status)) return 'warning';
  if (['FAILED', 'REJECTED', 'CANCELLED'].includes(status)) return 'danger';
  return 'info';
}

function fmt(value: unknown): string {
  if (!value) return '—';
  const time = Date.parse(String(value));
  return Number.isFinite(time)
    ? new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'short' }).format(new Date(time))
    : String(value);
}

export function NieboGuardMasterReviewSection() {
  const [items, setItems] = useState<Any[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ACTIONABLE');
  const [selected, setSelected] = useState<Any | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const result = await api.nieboguard.reviewMaster(300);
      setItems(result.items ?? []); setCounts(result.counts ?? {});
    } catch (cause) { setError(cause); } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const actionableStatuses = new Set(['READY_FOR_REVIEW', 'NEEDS_REVIEW', 'READY', 'SCRIPT_READY', 'FAILED']);
  const filtered = items.filter((item) => (typeFilter === 'ALL' || item.type === typeFilter) && (
    statusFilter === 'ALL' ||
    (statusFilter === 'ACTIONABLE' ? actionableStatuses.has(item.status) : item.status === statusFilter)
  ));
  const draftCount = items.filter((item) => item.status === 'DRAFT').length;
  const pager = usePagination(filtered, 12);

  const act = async (item: Any, action: string) => {
    const reason = action === 'reject' ? window.prompt('Powód odrzucenia / anulowania', 'Odrzucono w Master Review') : undefined;
    if (action === 'reject' && !reason) return;
    setBusy(`${item.type}:${item.id}:${action}`); setError(null); setMessage('');
    try {
      await api.nieboguard.reviewAction({ type: item.type, id: item.id, action, reason: reason || undefined });
      setMessage(action === 'approve' ? 'Materiał zaakceptowany.' : action === 'reject' ? 'Materiał odrzucony.' : action === 'queue' ? 'Materiał dodany do kolejki.' : 'Zlecono ponowną generację.');
      setSelected(null);
      await load();
    } catch (cause) { setError(cause); } finally { setBusy(null); }
  };

  const queueReadyVisuals = async () => {
    const ready = items.filter((item) => item.type === 'visual' && item.status === 'READY' && item.actions?.approve);
    if (!ready.length) return;
    if (!window.confirm(`Zakolejkować ${ready.length} gotowych grafik do publikacji?`)) return;
    setBusy('bulk-visuals'); setError(null); setMessage('');
    let queued = 0;
    const failures: string[] = [];
    for (const item of ready) {
      try { await api.nieboguard.reviewAction({ type: 'visual', id: item.id, action: 'approve' }); queued += 1; }
      catch (cause) { failures.push(`${item.title}: ${cause instanceof Error ? cause.message : String(cause)}`); }
    }
    setMessage(`Zakolejkowano ${queued} grafik.${failures.length ? ` Błędy: ${failures.slice(0, 3).join(' · ')}` : ''}`);
    await load();
    setBusy(null);
  };

  if (loading) return <Spinner label="Ładowanie Master Review…" />;

  return <div className="nb-list">
    {error ? <ErrorAlert error={error} /> : null}
    {message ? <div className="alert alert--success">{message}</div> : null}
    <Card title="Master Review — wszystko w jednym miejscu" actions={<div className="btn-row"><Button size="sm" variant="success" onClick={() => void queueReadyVisuals()} disabled={busy !== null || !items.some((item) => item.type === 'visual' && item.status === 'READY' && item.actions?.approve)}>Kolejkuj gotowe grafiki</Button><Button size="sm" onClick={() => void load()} disabled={loading}>Odśwież</Button></div>}>
      <p className="nb-stat-note">Akceptacja od razu odkłada materiał do kolejki publikacji (dla grafiki: rasteryzacja + slot). Po akceptacji pozycja znika z tej listy. Szkice DRAFT są osobnym filtrem i nie mają aktywnego linku publicznego do czasu akceptacji.</p>
      <div className="nb-grid nb-grid--stats">
        <div className="nb-stat"><strong>{counts.total ?? 0}</strong><span>Razem do decyzji</span></div>
        <div className="nb-stat"><strong>{counts.production ?? 0}</strong><span>Produkcje</span></div>
        <div className="nb-stat"><strong>{counts.visual ?? 0}</strong><span>Grafiki social</span></div>
        <div className="nb-stat"><strong>{counts.packageItem ?? 0}</strong><span>Elementy pakietów</span></div>
        <div className="nb-stat"><strong>{counts.article ?? 0}</strong><span>Artykuły</span></div>
        {draftCount ? <div className="nb-stat"><strong>{draftCount}</strong><span>Szkice (DRAFT)</span></div> : null}
      </div>
      <div className="nb-row">
        <label className="field"><span>Typ</span><select value={typeFilter} onChange={(event) => { setTypeFilter(event.target.value); pager.setPage(1); }}><option value="ALL">Wszystkie</option><option value="production">Produkcje</option><option value="visual">Grafiki social</option><option value="package-item">Elementy pakietów</option><option value="article">Artykuły</option></select></label>
        <label className="field"><span>Status</span><select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); pager.setPage(1); }}><option value="ACTIONABLE">Do decyzji</option><option value="ALL">Wszystkie</option><option value="DRAFT">Szkice DRAFT</option><option value="READY_FOR_REVIEW">Do akceptacji</option><option value="NEEDS_REVIEW">Do przeglądu</option><option value="READY">Gotowe</option><option value="SCRIPT_READY">Scenariusz gotowy</option><option value="FAILED">Błędy</option></select></label>
      </div>
    </Card>

    {!filtered.length ? <EmptyState title="Brak materiałów do decyzji" hint="Autopilot nie ma teraz nic do akceptacji." /> : <Card title={`Kolejka (${filtered.length})`}>
      <div className="table-wrap"><table className="table"><thead><tr><th>Materiał</th><th>Typ</th><th>Status</th><th>Źródło</th><th>Aktualizacja</th><th>Akcje</th></tr></thead><tbody>
        {pager.pageRows.map((item) => <tr key={`${item.type}:${item.id}`}>
          <td><strong>{item.title}</strong>{item.error ? <div className="nb-item__meta nb-danger">{String(item.error).slice(0, 160)}</div> : null}<div className="nb-item__meta">{item.id}</div></td>
          <td><Badge tone="info">{TYPE_LABELS[item.type] ?? item.type}</Badge><div className="nb-item__meta">{KIND_LABELS[item.materialKind] ?? item.materialKind}</div></td>
          <td><Badge tone={statusTone(item.status)}>{item.status}</Badge></td>
          <td>{item.source ?? '—'}{item.packageId ? <div className="nb-item__meta">pakiet: {item.packageId}</div> : null}</td>
          <td>{fmt(item.updatedAt)}</td>
          <td><div className="btn-row">
            <Button size="sm" variant="ghost" onClick={() => setSelected(item)}>Podgląd</Button>
            {item.actions?.approve ? <Button size="sm" variant="primary" disabled={busy !== null} onClick={() => void act(item, 'approve')}>{item.type === 'visual' ? 'Akceptuj + kolejka' : 'Akceptuj'}</Button> : null}
            {item.actions?.queue ? <Button size="sm" variant="success" disabled={busy !== null} onClick={() => void act(item, 'queue')}>Do kolejki</Button> : null}
            {item.actions?.regenerate ? <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => void act(item, 'regenerate')}>{item.type === 'production' && !item.previewUrl ? 'Renderuj' : 'Generuj ponownie'}</Button> : null}
            {item.actions?.reject ? <Button size="sm" variant="danger" disabled={busy !== null} onClick={() => void act(item, 'reject')}>Odrzuć</Button> : null}
          </div></td>
        </tr>)}
      </tbody></table></div>
      <div className="nb-row nb-row--between"><span className="nb-stat-note">Strona {pager.page} z {Math.max(1, Math.ceil(pager.total / pager.pageSize))}</span><div className="btn-row"><Button size="sm" variant="ghost" disabled={pager.page <= 1} onClick={() => pager.setPage(pager.page - 1)}>Poprzednia</Button><Button size="sm" variant="ghost" disabled={pager.page >= Math.max(1, Math.ceil(pager.total / pager.pageSize))} onClick={() => pager.setPage(pager.page + 1)}>Następna</Button></div></div>
    </Card>}

    {selected ? <Card title={`Podgląd — ${selected.title}`} actions={<Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Zamknij</Button>}>
      <div className="nb-grid nb-grid--2">
        <div>
          <p><strong>Typ:</strong> {TYPE_LABELS[selected.type] ?? selected.type} / {KIND_LABELS[selected.materialKind] ?? selected.materialKind}</p>
          <p><strong>Status:</strong> {selected.status}</p>
          <p><strong>Źródło:</strong> {selected.source ?? '—'}</p>
          <p><strong>ID:</strong> {selected.id}</p>
          {selected.details?.publicUrl ? <p><a href={selected.details.publicUrl} target="_blank" rel="noreferrer">Otwórz artykuł publiczny</a></p> : null}
          {selected.details?.sourceUrl ? <p><a href={selected.details.sourceUrl} target="_blank" rel="noreferrer">Źródło materiału</a></p> : null}
          {selected.previewUrl ? <p><a href={selected.previewUrl} target="_blank" rel="noreferrer">Otwórz materiał w nowej karcie</a></p> : null}
          {selected.details?.excerpt ? <p className="nb-stat-note">{selected.details.excerpt}</p> : null}
        </div>
        <div>
          {selected.previewUrl ? (String(selected.mimeType ?? '').startsWith('video/') ? <video src={selected.previewUrl} controls style={{ width: '100%', maxHeight: 360, background: '#000' }} /> : <img src={selected.previewUrl} alt={selected.title} style={{ width: '100%', maxHeight: 360, objectFit: 'contain', background: '#111' }} />) : <p className="nb-stat-note">Brak pliku podglądowego — materiał może być jeszcze w renderze lub to artykuł.</p>}
        </div>
      </div>
      {selected.details?.platformCopy ? <pre className="nb-mono" style={{ whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto' }}>{JSON.stringify(selected.details.platformCopy, null, 2)}</pre> : null}
      {selected.details ? <details><summary>Pełne metadane</summary><pre className="nb-mono" style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>{JSON.stringify(selected.details, null, 2)}</pre></details> : null}
    </Card> : null}
  </div>;
}
